import React, { FC, memo, useMemo } from 'react';
import styled from 'styled-components';
import { Header } from '@tanstack/react-table';
import { Skeleton, SkeletonProps } from '../Skeleton/Skeleton';
import { defaultColumnStyling, getAlignStyle } from './utils/getStyling';
import { getCellWidth, sanitizeId } from './utils/getCellWidth';
import * as DataTableTypes from './utils/DataTableTypes';
import { Tr } from './DataTableBasicElements/Tr';
import { Td } from './DataTableBasicElements/Td';

interface Props {
  headers: Header<object, unknown>[] | undefined;
  isFetching: boolean | undefined;
  tableHeight: string | undefined;
  border: DataTableTypes.DataTableBorder | undefined;
  rowHeight: DataTableTypes.DataTableRowHeight | undefined;
  resizeTableColumns: boolean;
}

interface HeaderDetails {
  width: string | number;
  isCheckbox: boolean | undefined;
  isExpander: boolean | undefined;
  isMenu: boolean | undefined;
  defaultWidth: number | undefined;
  align: DataTableTypes.ColumnAlignment | undefined;
}

export const DataTableSkeleton: React.FC<React.PropsWithChildren<Props>> = ({
  rowHeight,
  border,
  headers,
  isFetching,
  tableHeight,
  resizeTableColumns
}) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  const headersDetails: HeaderDetails[] = useMemo(
    () =>
      headers?.map((header) => {
        const sanitizedId = sanitizeId(header.id);
        const meta = header.column.columnDef.meta;
        return {
          isCheckbox: header.id?.includes('selection'),
          isExpander: header.id?.includes('expander'),
          isMenu: header.id?.includes('menu'),
          width: resizeTableColumns ? `calc(var(--header-${sanitizedId}-size) * 1px)` : getCellWidth(header.getSize),
          align: meta?.align,
          defaultWidth: resizeTableColumns ? undefined : meta?.defaultWidth
        };
      }) ?? [],
    [headers?.length, resizeTableColumns]
  );

  return (
    <StyledRoot $isFetching={isFetching} data-test="data-table-skeleton">
      <Skeletons
        resizeTableColumns={resizeTableColumns}
        rowHeight={rowHeight}
        border={border}
        details={headersDetails}
        tableHeight={tableHeight}
      />
    </StyledRoot>
  );
};

// Limit rows and columns renders for performance. Particularly for tables with huge amount of columns.
const MAX_ROWS_TO_RENDER = [...Array(21)];
const ALL_ROWS = [...Array(30)];

interface SkeletonProp {
  details: HeaderDetails[] | undefined;
  tableHeight: string | undefined;
  border: DataTableTypes.DataTableBorder | undefined;
  rowHeight: DataTableTypes.DataTableRowHeight | undefined;
  resizeTableColumns: boolean;
}

// Keep in separate component with memo to avoid re-renders
const Skeletons: FC<React.PropsWithChildren<SkeletonProp>> = memo(
  ({ details, tableHeight, border, rowHeight, resizeTableColumns }) => (
    <>
      {(tableHeight === 'content' ? ALL_ROWS : MAX_ROWS_TO_RENDER).map((_, trIndex) => (
        <Tr
          resizeTableColumns={resizeTableColumns}
          isSelected={false}
          isSkeleton
          rowHeight={rowHeight}
          key={`${trIndex}_skeleton_tr`}
          dataTest="data-table-row-skeleton"
        >
          {details?.map((detail, i) => {
            const fixedTableWidth =
              !resizeTableColumns && detail?.defaultWidth
                ? {
                    width: `${detail.defaultWidth}px`,
                    minWidth: `${detail.defaultWidth}px`,
                    flex: `1 1 ${detail.defaultWidth}px`
                  }
                : {};

            const newDefaultWidth = resizeTableColumns
              ? {
                  width: detail.width,
                  minWidth: detail.width,
                  flex: `1 1 ${detail.width}`
                }
              : {};
            return (
              <Td
                resizeTableColumns={resizeTableColumns}
                dataTest="data-table-skeleton-td"
                columnName="DataTableSkeleton"
                border={border}
                key={i}
                style={{
                  ...defaultColumnStyling,
                  width: resizeTableColumns ? undefined : detail.width,
                  ...getAlignStyle(detail.align),
                  ...fixedTableWidth,
                  ...newDefaultWidth
                }}
              >
                <Skeleton {...getSkeletonProps(detail)} />
              </Td>
            );
          })}
        </Tr>
      ))}
    </>
  ),
  (prevProps, currentProps) => prevProps.details?.length === currentProps.details?.length
);

const StyledRoot = styled.div<{ $isFetching?: boolean }>`
  position: absolute;
  width: 100%;
  height: auto;
  background-color: ${(e) => e.theme.colorSurface}; // 255, 255, 255
  color: ${(e) => e.theme.colorOnSurface};
  z-index: 4;
  /* Do not re-render skeleton every time. Use CSS instead */
  display: ${({ $isFetching }) => ($isFetching ? 'inherit' : 'none')};
`;

const SKELETON_WIDTH_RANGE = [50, 80] as [number, number];
function getSkeletonProps(details: HeaderDetails): SkeletonProps {
  if (details.isExpander) {
    return {
      height: 28,
      width: 16
    };
  }
  if (details.isCheckbox) {
    return {
      height: 28,
      width: 16
    };
  }
  if (details.isMenu) {
    return {
      height: 32,
      width: 4
    };
  }
  return {
    height: '100%',
    randomWidthInPercentage: SKELETON_WIDTH_RANGE
  };
}
